import {confirmationDialog} from 'include-dialogs.js';

//WICHTIG: 
//    Es wird _nicht_ per Babel transpiled. Damit ist es zwar weniger browser-abwärtskompatibel, aber aync/await ist mit Babel kompliziert und erfordert
//    NPM-Module zum Arbeiten. Und wenn dieses Script in älteren Browsern einfach nicht funktioniert, ist das nicht so wild. Dann gibt's eben keinen Countdown,
//    sondern nur die normale Anzeige.

//Dieses Script muss immer deferred geladen werden. Derzeit wird noch die Codekit-Version als Standard-JS geladen, wobei das "defer"-Attribut mit angegeben
//werden muss.  (Die Einbindung erfolgt durch Injektion im Java-Code (JQueryAndCoIntegration.includeBearbeitungsendeCountdownScript()), und dort
//wird "defer"-Einbindung sichergestellt.
//Sollte es mal als JS-Modul direkt eingebunden werden, wird es implizit deferred.

//Keys für SessionStorage:
const KEY_DISCLAIMER_ACK = "deadline.disclaimer.acknowleged";
const KEY_PREFIX_TIMERSTATE = "deadline.countdown.state.";
const KEY_PREFIX_CLIENT_DEADLINE = "deadline.client.timestamp.";
const KEY_PREFIX_TICKS_SINCE_LAST_SYNC = "deadline.ticks.";
const KEY_PREFIX_REQUESTS_SINCE_LAST_SYNC = "deadline.requests.";
const KEY_PREFIX_SERVER_DEADLINE = "deadline.server.timestamp.";

//Diese Session Storage-Eintrage sind dazu da, dass gewisse Daten nicht bei jedem weiteren Seitenaufruf neu ermittelt werden müssen.
//Ganz wichtig ist der Timer-State: Wenn der bei jedem neuen Get- oder Post-Request verloren ginge, könnte der Timer nicht eingeblendet gehalten
//werden und müsste jedes Mal wieder der Disclaimer weggeklickt werden oder wäre nicht realisierbar.
//Weniger wichtig und nur zur Entlastung des Servers von unnötig vielen Ajax-Requests sind die beiden anderen Keys. ClientTimestamp sorgt dafür,
//dass das Ergebnis eines Ajax-Uhrzeitabgleichs auch eine Zeit lang vorgehalten wird und nicht nach jedem Seitenaufruf immer wieder gleich ein
//Ajax-Request zur Servertime folgt. Und Deadline-Ticks zeigen an, wann der nächste Ajax-Sync erfolgen soll. Dies ist vor dem Hintergrund des gecachten
//Timestamps wichtig, weil eben ein Reload/Seitenaufruf keinen Re-Sync erzwingt, dass dann eben doch nach einer festgelegten Zeit (sofern der Countdown
//überhaupt aktiv ist, also genauer nicht nach einer Zeit, sondern einer bestimmten Anzahl von Countdown-Ticks) ein Re-Sync stattfindet, egal ob
//in diesem Zeitraum die Seiten geöffnet geblieben ist oder reloaded bzw. ersetzt wurde.
//Ausgelesen werden diese Werte jeweils nur direkt bei der Initialisierung, also dem ersten Aufruf von initCountdown(). Dann läuft das Script unabhängig
//davon weiter (updatet die Session-Entries aber ab und zu, wobei die Ticks nicht sekündlich auch im Storage aktualisiert werden sollen, sondern aus
//Performancegründen nur z.B. alle 10 Ticks o.ä.)
//Die Server-Timestamp wird ebenfalls gecacht, für den Fall, dass die Frist serverseitig bearbeitet (z.B. verlängert) wird. Dann soll beim nächsten
//Request, wenn der Server-Timestamp (Bearbeitungsende) wieder mit übertragen wird, dieser mit dem gecachten Wert verglichen werden. Bei Abweichung
//soll ein sofortiger Refresh des Countdowns stattfinden und nicht bis zu 5 Minuten oder 5 Requests (s.u.) mit dem Refresh gewartet werden, in welchem
//Fall die Countdown-Anzeige nicht mehr zur Bearbeitungsende-Anzeige passen würde.

//Hinweis: Dieser Ansatz ist **nur für SessionStorage** gedacht!
//Falls jemand mehrere Tabs offen hat, hat jedes Tab seinen _eigenen_ SessionStorage, die kommen sich also nicht in die Quere!
//Aber falls jemand im _selben_ Tab einen anderen Kurs ansurft, dürfen auch dort die vom früheren Heft gecachten Daten nicht angezeigt werden.
//Daher sind alle heftlokalen Props (alles bis auf das Acknowledgement des Disclaimers -- der nur einmalig (pro Tab) und nicht für jedes Heft immer wieder 
//angezeigt werden soll) hier nur als Key-Präfixe definiert, an die jeweils noch ein HeftKey anzuhängen ist, siehe Init-Methode.


/**
 * Nach wievielen Sekunden (genauer: Ticks des Countdowns) soll der Timer mittels eines neuen Ajax-Requests sicherheitshalber neu synchronisiert werden?
 * Derzeit: 5 Minuten.
 */
const RELOAD_AFTER_SECONDS = 5 * 60;
//const RELOAD_AFTER_SECONDS = 30; //Nur für Testzwecke!

/**
 * Ähnlich, aber für anderen Fall: Wenn gar kein Countdown-Timer zur Verfügung steht, weil der Counter abgelaufen oder >1 Tag in der Zukunft liegt,
 * dann gibt es ja keinen automatischen Re-Sync nach obiger Tick-Anzahl, weil mangels angezeigtem Counter keine Ticks stattfinden.
 * Dennoch kann es ja sein (wenn auch unwahrscheinlich), dass z.B. das Bearbeitungsende im Hintergrund verstellt wird oder sich die Systemzeit nochmal
 * ändert. Auch in solchen Fällen soll ein Refresh erfolgen, aber auch nicht bei jedem einzelnen Request, sondern nur bei jedem n-ten Request. Diese
 * Zahl n wird hier eingestellt:
 */
const RELOAD_AFTER_N_REQUESTS_IF_NOT_COUNTING = 5;

/**
 * Die Keep-Alive-Requests bei offener Session (z.B. SSO oder LTI) sollen nicht unendlich weiterlaufen, wenn jemand die Seite einfach nie schließt.
 * Hier wird das Maximum der Keep-Alive-Requests eingestellt. Bei knapp 30 Minuten Session-Dauer entsprächen also z.B. 48 Requests knapp 24 Stunden.
 */
const MAX_KEEPALIVE_PINGS = 50;

let keepAliveCounter = 0;


/** 
 * Berechnung eines Deltas := Clientzeit - Serverzeit (in Millisekunden), so dass also gilt: Clientzeit - Delta = Serverzeit  bzw. Serverzeit + Delta = Clientzeit.
 * Dient dazu, den Countdowntimer aus der Client-Systemuhr jederzeit korrekt ableiten zu können.
 * Für den Countdown soll ja die verbleibende Bearbeitungszeit als Differenz aus der Systemzeit des Browsers und der Abgabefrist berechnet werden. 
 * Letztere liegt relativ zur Serverzeit vor, und diese serverzeit-basierte Abgabefrist muss zunächst in eine clientzeit-basierte Abgabefrist umgerechnet werden.
 * Das hier berechnete Delta gibt an, **wieviele Millisekunden zur (Abgabefrist in) Serverzeit zu addieren sind**, um die (Abgabefrist in) Clientzeit zu ermitteln.
 * Beispiel: Eine Klausur starte um exakt 10:00 Uhr Serverzeit, die Clientzeit sei aber aufgrund erstens einer Zeitzonenverschiebung +1h eigentlich 11:00 Uhr, 
 * aufgrund einer sogar um 2 Minuten nachgehenden Uhr aber 10:58.
 * D.h. es ergibt sich ein (später in Millisekunden auszugebendes) Delta von 58 Minuten (Clientzeit - Serverzeit)
 * Das Bearbeitungsende sei 12:00 Uhr Serverzeit, also demnach 12:58 Clientzeit. D.h. der Countdown soll ab (jeweils Clientzeit) 10:58 bis 12:58 herunterzählen,
 * was wieder genau den 2h Bearbeitungszeit entspricht.
 * @param {String} ajaxUrl URL für Ajax-GetService, der ein JSON mit der Serverzeit ausgibt
 * @returns {int} Delta = Clientzeit - Serverzeit in Millisekunden
 */
async function timeSyncDelta(ajaxUrl) {
    //Zur Latenz bei der Zeitmessung:
    //Angenommen, beide Systemuhren (Server und Client) gingen _exakt gleich_, aber es kommt z.B. zu einer Latenz von genau 1 Sekunde, genauer:
    //einer der beiden gemessenen Zeistempel weicht durch Übertragungslatenzen bei der Serverzeitmessung um genau eine Sekunde vom anderen ab.
    //Wenn nun Delta = +1 gälte, würde der Countdown herunterzählen zu (0:00 erreichen um) Abgabefrist (in Serverzeit) + 1s. D.h. der Countdown würde eine Sekunde 
    //Extra-Bearbeitungszeit versprechen, die es aber nicht gibt.
    //Daher soll vielmehr Delta = -1 gelten, der Countdown also lieber einer Sekunde _vor_ Abgabefrist ablaufen.
    //Da Delta = Clientzeit - Serverzeit nun negativ sein soll, muss also die (gemessene) Clientzeit < (gemessene) Serverzeit sein. 
    //(Es geht hier um die gemessenen Zeitstempel, die _tatsächlichen_ Zeiten seien ja laut Annahme identisch.)
    //Dazu ist also die Clientzeit zu messen, _bevor_ die Messung der Serverzeit angestoßen wird. Wird dann z.B. durch die Verzögerungen / Latenzen
    //Wird dann z.B. zur Clientzeit 10:00:00 der Ajax-Request gesendet und durch Verbindungs-Latenzen, die Serverzeit erst um 10:00:01 gemessen und
    //dann gegen 10:00:02 vom Client wieder empfangen, gilt clientTime = 10:00:00 und serverTime = 10:00:01 und somit Delta = -1s.
    //Angenommen, die Abgabefrist wäre z.B: 12:00 Uhr.
    //Im Idealfall ohne Latenzen sollte der Countdown ja exakt bis zur Einsendefrist (z.B. 12:00) Clienttime (die ja laut Annahme exakt der Servertime entspreche)
    //herunterzähen. Mit diesem Delta wird nun aber bis 12:00:00 + (-1s) = 11:00:59 heruntergezählt, der Countdown läuft 1s zu früh ab, besser als 1s zu spät.

    //TL;DR: _Erst_ die Client-Systemzeit messen, _dann_ JavaScript-Call für Servertime-Ermittlung senden.

    //Theoretisch könnte man ggf. die Messlatenz etwas reduzieren, wenn man erst den Ajax-Request sendet, dann sofort während der Wartezeit auf eine
    //Antwort die Clientzeit misst und dann auf die Antwort wartet. Aber das Verbesserungspotential dürfte marginal sein (im Millisekundenbereich oder weniger),
    //und es könnte ggf. dazu kommen, dass, wenn die Scriptinterpretierung vom Browser angehalten wird zwischen diesen beiden Schritten, die Serverzeitmessung
    //doch vor der Clientzeitmessung stattfindet (unwahrscheinlich, aber theoretisch denkbar und unbedingt auszuschließen!). Daher lasse ich da die Finger von!

    const clientMillis = Date.now();
    const ajaxResponse = await fetch(ajaxUrl, {
        cache: "no-cache", //Wichtig, sonst könnte sich der Countdown immer weiter verlängert / mit jedem Request wieder von der letzten Zeit an rückwärts zählen, 
                            //also zuviel Bearbeitungszeit anzeigen!
        credentials: "omit" //Der Service ist loginfrei, also brauchen keine Credentials mitgesendet zu werden.
    });
    const json = await ajaxResponse.json();
    const serverMillis = json.millis;

    //Noch ein Problem: Angenommen, die Latenz war beinahe null, d.h. serverMillis und clientMillis weichen wirklich nur (fast exakt) um den
    //Uhrzeitversatz ab, bzw. bei exakt gleich gehenden Uhren gar nicht.
    //Nun wird zwar die exakt korrekte Countdown-Zeit berechnet und beim Rendering auch ausgegeben, bleibt dann aber 1 Sekunde angezeigt, bevor per
    //Timeout ein Refresh der Anzeige stattfindet.
    //Nun könnte es sein, dass der Countdown eigentlich schon Millisekunden nach Rendering der korrekten Restzeit um 1 herunterzählen müsste, das
    //Refresh-Event also bis zu knapp einer Sekunde verspätet eintritt, der Countdown also effektiv dadurch eine (knappe) Sekunde zuviel Restzeit anzeigt.
    //Subtrahiere daher noch eine Toleranz von 1 Sekunde vom Delta (so dass clientDeadline := serverDeadline + delta eine Sekunde früher liegt)!
    return clientMillis - serverMillis - 1000;
}

const countdownEl = document.querySelector("#bearbeitungsendecountdown .countdown");

async function initCountdown() { 
    const storage = window.sessionStorage;

    //Erwarte HTML-Stuktur wie 
    //<span id="bearbeitungsendecountdown">
    //  ...Bearbeitungsende-Anzeige ...
    //  <span class="countdown"></span>
    //  <button class="... start" ...></button>
    //  <button class="... stop" ...></button>
    //</span>

    //In das .countdown-Span soll dann Code injiziert werden, s.u.

    const aufgabenheftheader = document.getElementById("aufgabenheftheader");
    const containerEl = document.getElementById("bearbeitungsendecountdown");
    const timeEl = document.querySelector("#bearbeitungsendecountdown .time");
    const startBtn = document.querySelector("#bearbeitungsendecountdown .start");
    const stopBtn = document.querySelector("#bearbeitungsendecountdown .stop");
    const ajaxUrl = countdownEl.dataset.url;
    const serverDeadline = parseInt(countdownEl.dataset.tillMillis);
    const systemName = countdownEl.dataset.systemName;
    const heftKey = countdownEl.dataset.heftKey;
    const sessionTimeoutSecs = countdownEl.dataset.sessionTimeoutSecs;
    const sessionKeepAliveUrl = countdownEl.dataset.sessionKeepaliveUrl;
    //Notiz: Typ von Data-Attribut-Values ist immer String, sofern der Key vorhanden ist, vgl. MDN:
    //>The dataset read-only property of the HTMLElement interface provides read/write access to custom data attributes (data-*) on elements. 
    //>It exposes a map of strings (DOMStringMap) with an entry for each data-* attribute.

    const KEY_TIMERSTATE = KEY_PREFIX_TIMERSTATE + heftKey;
    const KEY_CLIENT_DEADLINE = KEY_PREFIX_CLIENT_DEADLINE + heftKey;
    const KEY_TICKS_SINCE_LAST_SYNC = KEY_PREFIX_TICKS_SINCE_LAST_SYNC + heftKey;
    const KEY_REQUESTS_SINCE_LAST_SYNC = KEY_PREFIX_REQUESTS_SINCE_LAST_SYNC + heftKey;
    const KEY_SERVER_DEADLINE = KEY_PREFIX_SERVER_DEADLINE + heftKey;

    async function ajaxGetClientDeadline() {
        //Mit dem folgenden Testcode kann z.B. der Mechanismus beim Reload in der Tick-Schleife getestet werden, der überlappende Nachladeaufrufe
        //unterbindet (also nicht jede Sekunde einen weiteren Ajax-Request startet, nur weil der vorherige nach 1s noch nicht fertig ist).
        //Auch dass beim Anklicken des activate()-Buttons _sofort_ der Counter angezeigt werden soll und erst im Hintergrund neu synchronisiert, statt
        //synchron neu zu synchronisieren (also darauf zu warten) und erst dann den Countdown anzuzeigen, lässt sich hiermit testen.
        //Zur Produktion muss das natürlich auskommentiert werden:
        //BEGIN TESTCODE
//        console.log("entering ajaxGlientDeadline");
//        await new Promise(resolve => window.setTimeout(resolve, 2000)); //2 Sekunden warten vor Ajax-Request, nur für Testzwecke!
        //END TESTCODE
        const delta = await timeSyncDelta(ajaxUrl);
        //BEGIN TESTCODE
//        console.log("exiting ajaxGlientDeadline");
        //END TESTCODE
        return serverDeadline + delta;
    }

    function serverDeadlineChanged() {
        let last = storage.getItem(KEY_SERVER_DEADLINE);
        return (typeof last === "string") && parseInt(last) !== serverDeadline;
    }

    async function loadClientDeadline() {
        let cdl = storage.getItem(KEY_CLIENT_DEADLINE);
        if (typeof cdl === "string" && !serverDeadlineChanged())
            cdl = parseInt(cdl);
        else {
            cdl = await ajaxGetClientDeadline();
            //Damit im Zweifel nur beim ersten Laden der Seite bei noch inaktivem Timer auch ein "proaktiver" Ajax-Sync stattfindet, soll das Ergebnis
            //sofort im Session Storage abgelegt werden und nachfolgende Aufrufe bei Seiten-Reloads oder Seiten-Navigationen finden dann diesen
            //Eintrag und synchronisieren nicht erneut (erst nach Aktivierung des Timers und Aufruf von updateDeadline()).
            storage.setItem(KEY_CLIENT_DEADLINE, cdl);
            storage.setItem(KEY_SERVER_DEADLINE, serverDeadline);
        }
        return cdl;
    }

    function loadTicks() {
        let ticks = storage.getItem(KEY_TICKS_SINCE_LAST_SYNC);
        return (typeof ticks === "string") ? parseInt(ticks) : 0;
        //In diesem Fall muss bei fehlendem Entry nichts in den Storage geschrieben werden, denn ein fehlender Eintrag ist äquivalent zu einem Eintrag mit Wert 0.
    }

    function loadRequestsSinceLastSync() {
        let req = storage.getItem(KEY_REQUESTS_SINCE_LAST_SYNC);
        return (typeof req === "string") ? parseInt(req) : 0;
    }

    let clientDeadline = await loadClientDeadline();    
    let ticksSinceLastSync = loadTicks();
    let requestsSinceLastSync = loadRequestsSinceLastSync();


    //valuecontainer -> Ein dynamisch ins countdownEl einzufügendes Element zur Countdown- oder Textanzeige mit class="value" (so dass z.B. per CSS
    //formatierbar ist, ob dieses in Klammern hinter die Frist oder ohne Klammern in eine neue Zeile gesetzt werden soll o.ä.)
    //Der valuecontainer soll wiederum _zwei_ Text-Nodes als Kindelemente enthalten, von denen eines immer leer ist:
    let valuecontainer = null;
    //1. myTextNode ist der erste Textnode und für den Live-Countdown (der sich sekündlich ändert) gedacht ist.
    let myTextNode = null;
    //2. myMessageTextNode ist der zweite Textnode und ist für Texte an Stelle des Countdowns gedacht wie "letzte Minute", "noch 5 Minuten", "noch 1 Stunden" etc.
    //   Der Grund, warum das ein eigener Node ist: Dieser soll aria-live="polite" bekommen, also seine Textmessages nicht nur anzeigen, sondern bei Verwendung
    //   eines Screen-Readers auch laut ansagen (polite = laufende Sprachausgaben sollen dafür nicht unbedingt sofort unterbrochen werden, die Message-Ansage
    //   darf auch ein wenig warten).
    let myMessageTextNode = null;

    //Zielstruktur also: <span class="countdown"><span class="value">{myTextNode}{myMessageTextNode}</span></span>

    let countdownIntervalTimer = null;
    let resyncPromise = null;

    function createInnerTextNodes() {
        if (!myTextNode) {
            valuecontainer = document.createElement("span");
            valuecontainer.setAttribute("class", "value");
            myTextNode = document.createElement("span");
            valuecontainer.appendChild(myTextNode);
            myMessageTextNode = document.createElement("span");
            myMessageTextNode.setAttribute("class", "message");
            myMessageTextNode.setAttribute("aria-live", "polite");
            valuecontainer.appendChild(myMessageTextNode);
            countdownEl.appendChild(valuecontainer);
        }
    }

    function removeInnerTextNodes() {
        if (valuecontainer) {
            countdownEl.removeChild(valuecontainer);
            valuecontainer = null;
            myTextNode = null;
            myMessageTextNode = null;
        }
    }

    function innerTextNode() {
        createInnerTextNodes();
        return myTextNode;
    }

    function innerMessageTextNode() {
        createInnerTextNodes();
        return myMessageTextNode;
    }

    function pad0(minsOrSecs) {
        return minsOrSecs < 10 ? "0" + minsOrSecs : minsOrSecs;
    }

    function setHeaderActive() {
        if (aufgabenheftheader !== null)
            aufgabenheftheader.classList.add("active");
    }

    function setHeaderInactive() {
        if (aufgabenheftheader !== null)
            aufgabenheftheader.classList.remove("active");
    }

    function setHeaderCountdownEnded() {
        aufgabenheftheader.classList.add("ended");
    }

    function updateDeadline(newClientDeadline) {
        clientDeadline = newClientDeadline;
        ticksSinceLastSync = 0;
        requestsSinceLastSync = 0;
        storage.setItem(KEY_CLIENT_DEADLINE, clientDeadline);
        storage.setItem(KEY_TICKS_SINCE_LAST_SYNC, "0");
        storage.setItem(KEY_REQUESTS_SINCE_LAST_SYNC, "0");
    }

    function active() {
        const state = storage.getItem(KEY_TIMERSTATE);
        return state === "true"; //Wichtig: Storage-Values sind immer Strings! Wenn setItem(Key, true) aufgerufen wird, wird diese Boolean-Konstante
        //zum String "true" konvertiert und muss hier entsprechend auch als String verglichen werden. Ein einfaches Prüfen auf "truthyness" ("if (state)")
        //würde auch für "false" true antworten, weil das auch ein nicht-leerer String ist! 
        //Umgekehrt wäre state === true immer false, weil state eben nie vom Typ Boolean ist.
    }

    /**
     * Manuelles Aktivieren des Countdowns per Button. (Dies wird <em>nicht</em> ausgeführt beim automatischen Start des Countdowns nach Seitenaufbau,
     * wenn ein Active-Flag im SessionStorage liegt.)
     * @returns {undefined}
     */ 
    async function activate() {
        const state = storage.getItem(KEY_TIMERSTATE);
        const ack = storage.getItem(KEY_DISCLAIMER_ACK) === "true";
        //Wenn schon ein state (false = ausgeblendet, true = eingeblendet) existiert, wurde der Disclaimer schonmal angezeigt und bestätigt.
        //Wenn state zwar noch null ist, also in _diesem_ Tab noch nicht confirmed wurde, _aber_ in einem anderen Heft (im selben Tab) schon einmal confirmed/acknowledged wurde,
        //was in einem speziellen Flag gesichert wurde, dann ist auch alles OK. 
        //Andernfalls muss der Disclaimer noch angezeigt und bestätigt werden.
        const confirmed = state !== null || ack || 
                //confirm(…)
                await confirmationDialog("Timer-Anzeige ohne Gewähr!", 
                "<p>Mit dieser Funktion blenden Sie einen Countdown-Timer zum Bearbeitungsende ein.<br> "
                + "Dieser läuft jedoch lokal in Ihrem Browser, außerhalb der Kontrolle des " + (systemName !== null ? systemName : "System") + "s "
                + "und daher ohne Gewähr!</p><p><em>Für die Wahrung der Abgabefrist ist immer nur die exakte Uhrzeit und nicht die Timer-Anzeige maßgeblich!</em></p>", 
                "Abbrechen", "Verstanden!", 
                "Timer-Anzeige OHNE GEWÄHR:\n\nMit dieser Funktion blenden Sie einen Countdown-Timer zum Bearbeitungsende ein. "
                + "Dieser läuft jedoch lokal in Ihrem Browser, außerhalb der Kontrolle des " + (systemName !== null ? systemName : "System") + "s "
                + "und daher ohne Gewähr!\nFür die Wahrung der Abgabefrist ist immer nur die exakte Uhrzeit und nicht die Timer-Anzeige maßgeblich!",
                "timeoutdisclaimer");
        if (confirmed) {
            storage.setItem(KEY_TIMERSTATE, true);
            if (!ack) //Flag war noch nicht gesetzt, soll nun gesetzt werden:
                storage.setItem(KEY_DISCLAIMER_ACK, true);
            startBtn.hidden = true;
            stopBtn.hidden = false;
            //updateCountdown() würde den stopBtn ebenfalls noch einblenden, aber ich mache den Buttontausch hier lieber "atomar" vor dem Update der Timer-Anzeige.
            //In jedem Fall wird der stopBtn in updateCountdown _nicht_ fokussiert. Ein explizites focus() soll nur nach manuellem Einblenden per startBtn
            //(also hier) erfolgen. (Der Fokus wechselt hiermit vom startBtn zum stopBtn).
            stopBtn.focus();
            //Das Einblenden des stopBtn erfolgt im folgenden Aufruf von updateCountdown().
            //Da ja schon in der Initialisierungsphase ein Ajax-Sync stattgefunden hat (der auch synchron (await!) erfolgte, also definitiv fertig sein muss),
            //kann der Countdown schonmal (ohne Latenz) angezeigt werden.
            updateCountdown();
            //Erst nach Einblenden des Countdowns sicherheitshalber noch einen asynchronen Resync starten. 
            //(Auch wenn _intern_ in dieser Funktion der Aufruf per "await" wieder gewissermaßen synchron ist, steht dies am Ende einer void async function, 
            //wartet also effektiv kein anderer Code mehr auf die Fertigstellung. Insgesamt also aynchron.)
            updateDeadline(await ajaxGetClientDeadline());
        }
    }

    function haltIntervalTimer() {
        if (countdownIntervalTimer !== null) {
            window.clearInterval(countdownIntervalTimer);
            countdownIntervalTimer = null;
        }
    }

    function stop() {
        haltIntervalTimer();
        storage.setItem(KEY_TIMERSTATE, false); //für Folgerequests den Sichtbarkeitsstatus setzen
        startBtn.hidden = false;
        stopBtn.hidden = true;
        removeInnerTextNodes();
        startBtn.focus();
        setHeaderInactive();
    }

    function getRemainingMillis() {
        return clientDeadline - Date.now();
    }

    function updateCountdown() {
        const remainingMillis = getRemainingMillis();

        if (remainingMillis < 0) {
            //Cache-Daten entfernen. Bei Folgerequests sollte eh kein HTML für den Counter mehr ausgeliefert werden, also das Script
            //nicht aktiv werden, es sei denn, das Bearbeitungsende wird geändert und liegt wieder in der Zukunft. Und in dem Fall
            //(sobald also entsprechende HTML-Elemente für den Counter wieder da sind) soll sofort die Deadline neu initialisiert werden.
            storage.removeItem(KEY_CLIENT_DEADLINE);
            storage.removeItem(KEY_REQUESTS_SINCE_LAST_SYNC);
            storage.removeItem(KEY_TICKS_SINCE_LAST_SYNC);
            haltIntervalTimer();
            removeInnerTextNodes();
            if (timeEl !== null)
                timeEl.classList.add("past");
            containerEl.removeChild(stopBtn);
            containerEl.removeChild(startBtn);
            setHeaderCountdownEnded();
        } else {
            //Zum Zerlegen verwende ich ein Date-Objekt wie folgt:
            //Wenn diese Zeitdifferenz in ein Date konvertiert wird, also als "Millisekunden" nach der Epoche (1.1.1970, 00:00 UTC) interpretiert wird,
            //dann zeigt (unter der Voraussetzung, dass der Monat noch auf 1 / Januar steht) der Tag (getDate()) die Anzahl der Tage plus 1 an, also 
            //1 = Frist ist heute, 2 = morgen, n = in n-1 Tagen etc.
            //Bei date=1 (heute) zeigt dann die "Uhrzeit am 1.1.1970" effektiv die noch anzuzeigenden Stunden, Minuten und Sekunden bis zum Einsendeschluss an.
            //Wichtig ist allerdings, dass die Komponenten als UTC-Zeit ausgelesen werden und nicht relativ zur lokalen Zeitzone des Systems, weil sonst
            //der Zeitzonenversatz zwischen dem 1.1.1970 UTC und der lokalen Systemzeit mit einberechnet würde.

            //Reminders zur API: 
            // 1.) Date.getDate() bzw. getUTCDate() ist bekloppt benannt und liefert den Tag des Monats von 1 bis 31 (während getDay den Wochentag ausgibt),
            // 2.) Date.getMonth() gibt (wie bei alten Java-Calendars) den Monat von 0 (Januar) bis 11 (Dezember) aus (wunderschön inkonsistent zum Tag, der 1-basiert ist).

            const d = new Date(remainingMillis);
            const remainingDays = d.getUTCDate() - 1;

            //Im Fall Monat > Januar (wobei Januar den Wert 0 hat, also im Fall Mont != 0) funktionieren die Berechnungen nicht.
            //Ein Countdown gibt aber mehr als 1 Monat vor Einsendeschluss auch nicht wirklich Sinn. In diesem fall das Countdown-Element einfach nichts tun.
            if (d.getUTCMonth() === 0) {
                if (remainingDays === 0) {
                    //Nun im Sekundenintervall herunterzählen.
                    let show = true;
                    if (!countdownIntervalTimer) { 
                        //Der IntervalTimer läuft noch nicht. 
                        //Prüfen, ob der Timer laut LocalStorage überhaupt aktiviert werden darf (dann Stop-Button einblenden)
                        //oder nicht (dann Timer aus- und Start-Button einblenden):
                        if (active()) {
                            //Der IntervalTimer ist lokal noch nicht gestartet, aber im Session Storage liegt ein Active-Flag, d.h. in einer früheren Seite
                            //wurde der Countdown-Timer aktiviert und soll bei Folge-Requests wie hier nun immer auch wieder eingeblendet werden, damit
                            //User nicht nach jeder Systeminteraktion / jedem Request wieder den Start-Button drücken müssen.
                            stopBtn.hidden = false;
                            //Starte den Intervall-Timer beim ersten Aufruf. Nichts zu tun, wenn der Timer schon aktiviert wurde
                            //und dieser updateCountdown-Aufruf nur ein Refresh auslösen soll.
                            countdownIntervalTimer = window.setInterval(updateCountdown, 1000); 
                            //Das ist nur ungefähr, der Timer wird eh nicht wirklich exakt im Sekunden-Rhythmus aufgerufen, sondern nach meiner Erfahrung jedesmal
                            //_mindestens/gut_ eine Sekunden warten. Daher wird der Countdown (ganz gerinfügig) zu langsam runterzählen und irgendwann dann mal 
                            //"eine Sekunde überspringen". Das ist aber durch die Sekundentoleranz alles gedeckt, er sollte so stets zwischen 1 bis 2 Sekunden vorgehen
                            //(also weniger Zeit anzeigen).
                            setHeaderActive();
                        } else {
                            startBtn.hidden = false;
                            show = false; //Timer i.F. nicht einblenden
                        }
                    }
                    if (show) {
                        const h = d.getUTCHours();
                        const m = d.getUTCMinutes();
                        const s = d.getUTCSeconds();
                        const n = innerTextNode();
                        const mn = innerMessageTextNode();
                        //Zu welchen Restzeiten x (0:x:00) soll eine "noch x Minuten"-Message ausgegeben werden?
                        const messageMinutes =   [5, 10, 15, 30, 45];
                        //Array mit den Minuten, innerhalb derer diese Message noch weiterhin angezeigt werden soll, also jeweils um 1 kleiner als die messageMinutes,
                        //außerdem noch der Eintrag 59 für die 59. Restminute zur "Weiter-Anzeige" der "noch 1 Stunde"-Message.
                        const followingMinutes = [4,  9, 14, 29, 44, 59];
                        //Für wieviele Sekunden soll so eine "noch x Minuten" oder "noch 1 Stunde"-Message an Stelle des Countdowns sichtbar sein (< 60!)
                        const messageDuration = 6;
                        if (h === 0 && m === 0) {
                            if (!mn.classList.contains("lastminute")) {
                                n.textContent = "";
                                mn.textContent = "letzte Minute!";
                                mn.classList.add("lastminute");
                            }
                        } else if (h === 0 && messageMinutes.includes(m) && s === 0) {
                            n.textContent = "";
                            mn.textContent = "noch " + m + " Minuten…";
                        } else if (h === 1 && m === 0 && s === 0) {
                            n.textContent = "";
                            mn.textContent = "noch 1 Stunde…";
                        } else if (mn.textContent === "" || s < 60 - messageDuration || !(followingMinutes.includes(m))) {
                            if (mn.textContent !== "")
                                mn.textContent = "";
                            n.textContent = h + ":" + pad0(m) + ":" + pad0(s);
                            n.setAttribute("aria-label", "noch " + h + " Stunden und " + m + " Minuten");
                            //Die Sekunden lasse ich mal weg, denn bis ein Reader die vorgelesen hat, sind sie schon nicht mehr aktuell.
                        }
                        //Nach einer gewissen Laufzeit den Timer sicherheitshalber neu synchronisieren,
                        //falls die lokale Systemuhr sehr ungenau geht (wie z.B. in VMWare zu beobachten) oder dort z.B. ein Zeitserver-Update stattgefunden hat.
                        //Diese Laufzeit wird nicht anhand der Systemuhr festgemacht, die ja verstellt worden sein könnte (und gerade dann wäre ein ReSync besonders sinnvoll!),
                        //sondern lokal anhand der "Ticks" des countdownIntervalTimers, also in etwa Sekunden.
                        //Auch exakt bei 2 Minuten lokaler Countdown-Restdauer nochmal einen Resync auslösen, falls der letzte Resync schon relativ lange her ist,
                        //der nächste im Intervall aber eben doch nicht mehr vor Beginn der letzten Minute getriggert würde. (Bei 5-Minuten-Takt z.B. also, wenn der
                        //letzte Resync ca. 5 Minuten vor Schluss stattfand, sollt selbst bei einem lokal zu langsamem Counter, der die "noch 2 Minuten"-Schwelle etwas
                        //zu spät erreicht, der Resync vermutlich noch vor dem Beginn der letzten Minute und der entsprechenden Einblendung/VoiceReader-Ansage stattfinden.)
                        if ((ticksSinceLastSync++ > RELOAD_AFTER_SECONDS || (h === 0 && m === 2 && s === 0)) && resyncPromise === null) {
                            resyncPromise = ajaxGetClientDeadline().then(updateDeadline).finally(() => resyncPromise = null);
                            //Falls dieser Request im Hintergrund länger dauert und zwischenzeitlich schon der nächste Tick (Aufruf dieser Methode 1s später)
                            //erfolgt, so gilt hier (für nachfolgende Aufrufe) resyncPromise != null, also wird kein paralleler Ajax-Call geschickt.
                            //Erst nach Abschluss (unabhängig vom Ergebnis) wird per finally die Referenz genullt.
                        } else if (ticksSinceLastSync % 10 === 0) {
                            storage.setItem(KEY_TICKS_SINCE_LAST_SYNC, ticksSinceLastSync);
                            //alle 10 Ticks aktualisiere auch den SessionStorage-Entry
                        }
                    } else {
                        //Dieser Zweig wird in zwei Fällen erreicht:
                        //1.) Die Seite wurde im Zustand "weniger als 24 Stunden to go, Timer wurde noch nicht aktiviert" aufgerufen oder
                        //2.) Die Seite wurde im Zustand remainingDays >= 1 aufgerufen und inzwischen aber ist die Zeit auf unter 24h gefallen und es
                        //    erfolgt ein timergesteuerter Counter-Refresh (siehe unten unter remainingDays > 0).
                        //Im ersten Fall ist noch gar keine Timeranzeige sichtbar, was OK ist, denn es soll ja auch keine anzeigt werden.
                        //Im zweiten Fall dagegen wird dort noch "morgen" angezeigt, was nun zurückgenommen werden muss.
                        //Also für alle Fälle einen ggf. existierenden myTextNode entfernen:
                        removeInnerTextNodes();
                    }
                } else if (remainingDays > 0) {
                    innerTextNode().textContent = remainingDays === 1 ? "morgen" : "in " + remainingDays + " Tagen";
                    //In diesem Fall setze ich kein Intervall, sondern einen einmaligen Timeout bis kurz 24 Stunden (oder auch ein Vielfaches davon) vor Frist.
                    //(Das ist in erster Linie wirklich dafür gedacht, falls remainingDays === 1 ist, dass dann der Refresh 24h vor Einsendeschluss stattfindet,
                    // wo dann die Anzeige von "morgen" zum Countdowntimer umspringen soll. Aber auch falls es noch > 1 Tage weg ist, kann ein einmal tägliches
                    // Update nicht schaden (wnn überhaupt jemand den Tab so lange offen lässt).)
                    //Dazu setzen wir den Tag im Date d auf 1 (1. Januar 1970), entfernen also alle "überschüssigen Tage", und es bleibt die Uhrzeit, die wieder in Millisekunden
                    //zurück konvertiert werden kann:
                    d.setUTCDate(1);
                    //d.getTime() gibt nun die EpochMillis zum Date aus.
                    window.setTimeout(() => updateCountdown(), d.getTime());
                    //In diesem Zweig finden noch keine Ticks statt, die "ticksSinceLastSync" inkrementieren könnten.
                    //Statt dessen werden hier einzelne Requests (Betreten dieses Zweigs, der im Gegensatz zu obigem ja nicht in einer Schleife (per Timer) 
                    //ausgeführt wird, sondern nur einmalig) gezählt und mit einer gesonderten oberen Schranke verglichen, um ab und zu einen Cache-Refresh auszulösen.
                    if (requestsSinceLastSync++ >= RELOAD_AFTER_N_REQUESTS_IF_NOT_COUNTING)
                        ajaxGetClientDeadline().then(updateDeadline); //Update im Hintergrund anstoßen, nicht abwarten.
                    storage.setItem(KEY_REQUESTS_SINCE_LAST_SYNC, requestsSinceLastSync);
                } else {
                    console.error("remainingDays NaN");
                }
            }
        }
    }

    function setKeepaliveTimeout(secs) {
        const millis = (secs - 30) * 1000; //30 Sekunden vor Ablauf der Session den Keep-Alive probieren.
        if (millis > 0 && keepAliveCounter++ <= MAX_KEEPALIVE_PINGS && getRemainingMillis() > 0) {
            setTimeout(requestKeepAlive, millis);
        }
    }
    
    async function requestKeepAlive() {
        const ajaxResponse = await fetch(sessionKeepAliveUrl, {
            cache: "no-cache", 
            credentials: "same-origin" //Echte Basic-Auth-Credentials würden vom Server ignoriert, ABER hier ist ja eine Session offen, und diese
            //Einstellung bewirkt auch, dass das Session-Cookie mit übertragen wird (was bei "omit" nicht der Fall wäre, da könnte der Request daher
            //nicht der Session zugeordnet werden und sie nicht verlängern!).
        });
        const json = await ajaxResponse.json();
        if (json.sessionOpen) {
            setKeepaliveTimeout(parseInt(json.timeoutSeconds));
        }
        
    }

    //Initialisieren
    //1. Eventhandler für Buttons
    startBtn.addEventListener("click", activate);
    stopBtn.addEventListener("click", stop);

    //2. Countdown "rendern"
    updateCountdown();
    
    //3. Ggf. Session-Keepalive einrichten
    //   (Die Data-Attribute sind optional und werden nur vom System (Java-seitig) dort eingefügt, wenn überhaupt eine Session offen ist, also ein KeepAlive probiert werden soll.)
    if (typeof sessionTimeoutSecs === "string" && typeof sessionKeepAliveUrl === "string")
        setKeepaliveTimeout(parseInt(sessionTimeoutSecs) - 30); //30 Sekunden vor Ablauf der Session den Keep-Alive probieren.
    
} //initCountdown()




//Nur etwas tun, wenn überhaupt ein .countdown-Childelement existiert. (Nach Ablauf der Bearbeitungsfrist kann ein #bearbeitungsendecoutndown-Container
//_ohne_ .countdown-Childelement existieren, weil ja dann gar kein Countdown mehr benötigt wird. Das Containerelement wird dann z.B. eine
//Frist-abgelaufen-Info o.ä. enthalten, die aber statisch ist und nicht von diesem Skript bedient werden muss.

if (countdownEl !== null) {
    initCountdown();
}